import * as THREE from "three";
import { rollDamage, ELEMENT_COLORS } from "../items/ItemFactory.js";
import { recordSave } from "./RelicMemory.js";
import { pruneInPlace } from "../core/util.js";

// ---- shared projectile resources (never disposed; reused across every shot) ----
// One unit sphere scaled per bolt, and one cached material per colour, so firing
// allocates nothing and leaks no GPU buffers. No per-bolt PointLight (that blew
// the light budget); the unlit MeshBasicMaterial already reads as a glowing mote.
const PROJ_GEO = new THREE.SphereGeometry(1, 8, 8);
const _projMats = new Map();
function projMat(color) {
  const key = String(color);
  let m = _projMats.get(key);
  if (!m) { m = new THREE.MeshBasicMaterial({ color }); _projMats.set(key, m); }
  return m;
}

/**
 * CombatSystem resolves attacks and owns projectiles. Melee and projectiles
 * also smash destructibles (barrels, crates, pots). Game wires the live
 * references each floor and handles what a broken prop drops.
 */
export class CombatSystem {
  constructor(scene) {
    this.scene = scene;
    this.projectiles = [];
    this.enemies = [];
    this.destructibles = [];
    this.player = null;
    this.level = null;
    this.effects = null;
    this.onEnemyKilled = () => {};
    this.onDestructibleBroken = () => {};
    this._grid = new Map();   // spatial hash of live enemies (cellKey -> [enemy])
    this._near = [];          // reused scratch buffer for neighbour queries
    this._cell = 2;
  }

  /** Rebuild the enemy spatial hash for this frame. Call once before updating
   *  enemies so separation is a neighbourhood query instead of an O(n²) scan. */
  buildEnemyGrid(cell = 2) {
    this._cell = cell;
    const g = this._grid; g.clear();
    for (const e of this.enemies) {
      if (e.dead) continue;
      const key = ((e.pos.x / cell) | 0) + "," + ((e.pos.z / cell) | 0);
      let bucket = g.get(key);
      if (!bucket) { bucket = []; g.set(key, bucket); }
      bucket.push(e);
    }
  }

  /** Live enemies in the 3×3 cell neighbourhood around (x,z). Returns a REUSED
   *  buffer — consume it synchronously (don't hold the reference across calls). */
  nearbyEnemies(x, z) {
    const cell = this._cell, out = this._near; out.length = 0;
    const cx = (x / cell) | 0, cz = (z / cell) | 0;
    for (let ix = cx - 1; ix <= cx + 1; ix++) {
      for (let iz = cz - 1; iz <= cz + 1; iz++) {
        const bucket = this._grid.get(ix + "," + iz);
        if (bucket) for (const e of bucket) out.push(e);
      }
    }
    return out;
  }

  reset(enemies, player, level) {
    this.clearProjectiles();
    this.enemies = enemies;
    this.player = player;
    this.level = level;
    this.destructibles = [];
  }

  clearProjectiles() {
    for (const p of this.projectiles) this.scene.remove(p.mesh);
    this.projectiles = [];
  }

  _power(player) { return player.weaponPower ? player.weaponPower() : (player.powerMult || 1); }

  /** Roll a critical hit (×1.7) from the player's research crit chance. */
  _critRoll(player, base) {
    if (Math.random() < (player.critChance || 0)) return { dmg: Math.round(base * 1.7), crit: true };
    return { dmg: Math.round(base), crit: false };
  }

  playerAttack(player, aimYaw) {
    const w = player.weapon;
    const fx = Math.sin(aimYaw), fz = Math.cos(aimYaw);
    if (w.wtype === "melee") {
      for (const e of this.enemies) {
        if (e.dead) continue;
        const dx = e.pos.x - player.pos.x, dz = e.pos.z - player.pos.z;
        const d = Math.hypot(dx, dz);
        if (d > w.range + e.radius) continue;
        const dot = (dx / d) * fx + (dz / d) * fz;
        if (dot > 0.3 || d < 1.5) { const c = this._critRoll(player, rollDamage(w) * this._power(player)); this._damageEnemy(e, c.dmg, player, w, c.crit); }
      }
      this._smashInArc(player, fx, fz, w.range + 0.6);
      if (this.effects) this.effects.burst({ x: player.pos.x + fx * 1.4, y: 1, z: player.pos.z + fz * 1.4 }, w.color, 5);
    } else {
      this._spawnProjectile(player, aimYaw, w);
    }
  }

  _smashInArc(player, fx, fz, reach) {
    for (const d of this.destructibles) {
      if (d.broken) continue;
      const dx = d.x - player.pos.x, dz = d.z - player.pos.z;
      const dd = Math.hypot(dx, dz);
      if (dd > reach + 0.4) continue;
      const dot = (dx / dd) * fx + (dz / dd) * fz;
      if (dot > 0.2 || dd < 1.6) this._break(d);
    }
  }

  _break(d) {
    d.broken = true;
    if (this.effects) this.effects.burst({ x: d.x, y: 1, z: d.z }, "#b58a4a", 14);
    this.onDestructibleBroken(d);
  }

  _spawnProjectile(player, aimYaw, w) {
    const mesh = new THREE.Mesh(PROJ_GEO, projMat(w.color));
    mesh.scale.setScalar(0.22);
    mesh.position.set(player.pos.x, 1, player.pos.z);
    this.scene.add(mesh);
    const speed = 22;
    const c = this._critRoll(player, rollDamage(w) * this._power(player));
    this.projectiles.push({
      mesh, vx: Math.sin(aimYaw) * speed, vz: Math.cos(aimYaw) * speed, life: w.range / speed, dmg: c.dmg, crit: c.crit,
      element: w.element || null, elementPower: w.elementPower || 0, aoe: w.aoe || 0, color: w.color,
    });
  }

  /** Apply a weapon's elemental enchantment as a status effect on an enemy. */
  _applyElement(e, element, power = 1) {
    if (!element || e.dead) return;
    if (element === "fire") e.applyBurn(5 * power, 3);
    else if (element === "frost") e.applySlow(0.5, 2.6);
    else if (element === "poison") e.applyPoison(4 * power, 4);
    else if (element === "shock") e.applyStun(0.4 + 0.3 * power);
    if (this.effects) this.effects.burst(e.pos, ELEMENT_COLORS[element] || "#ffffff", 6);
  }

  /** Damage dealt by a pet/companion (no player knockback); routes death normally. */
  petDamage(e, amt, color = "#ff7a3d") {
    if (!e || e.dead) return;
    amt = Math.round(amt);
    const dead = e.applyDamage(amt);
    if (this.effects) { this.effects.text(e.pos, `${amt}`, color); this.effects.burst(e.pos, color, 8); }
    if (dead) { if (this.effects) this.effects.burst(e.pos, color, 14); this.onEnemyKilled(e); }
  }

  /** Radial ability damage (Bulwark Slam, Whirlwind, Arcane Nova). Damage falls
   *  off toward the edge; an optional status ("stun"|"slow"|"burn") is applied to
   *  survivors. Also smashes destructibles in range. */
  aoeDamage(x, z, radius, dmg, color = "#ffffff", status = null) {
    for (const e of this.enemies) {
      if (e.dead) continue;
      const dd = Math.hypot(e.pos.x - x, e.pos.z - z);
      if (dd > radius + e.radius) continue;
      const fall = 1 - (dd / (radius + e.radius)) * 0.4;   // center hits harder
      const amt = Math.max(1, Math.round(dmg * fall));
      const dead = e.applyDamage(amt);
      if (this.effects) { this.effects.text(e.pos, `${amt}`, color); this.effects.burst(e.pos, color, 6); }
      if (!dead && status) {
        if (status === "stun") e.applyStun(1.2);
        else if (status === "slow") e.applySlow(0.5, 3);
        else if (status === "burn") e.applyBurn(4, 3);
      }
      if (dead) { if (this.effects) this.effects.burst(e.pos, color, 12); this.onEnemyKilled(e); }
    }
    for (const d of this.destructibles) {
      if (!d.broken && Math.hypot(d.x - x, d.z - z) < radius) this._break(d);
    }
  }

  /** Fire a PLAYER-owned bolt from an arbitrary origin (the ranger's turret).
   *  Reuses the normal projectile pipeline, so it damages enemies + smashes props. */
  spawnBolt(x, z, yaw, opts = {}) {
    const { dmg = 10, color = "#8bc34a", speed = 22, range = 13, element = null, elementPower = 0, aoe = 0 } = opts;
    const mesh = new THREE.Mesh(PROJ_GEO, projMat(color));
    mesh.scale.setScalar(0.18);
    mesh.position.set(x, 1, z);
    this.scene.add(mesh);
    this.projectiles.push({
      mesh, vx: Math.sin(yaw) * speed, vz: Math.cos(yaw) * speed, life: range / speed,
      dmg, crit: false, element, elementPower, aoe, color,
    });
    if (this.effects) this.effects.burst({ x, y: 1, z }, color, 4);
  }

  _damageEnemy(e, amt, player, weapon, crit = false) {
    const dead = e.applyDamage(amt);
    const col = "#" + e.type.color.toString(16).padStart(6, "0");
    if (this.effects) { this.effects.text(e.pos, crit ? `${amt}!` : `${amt}`, crit ? "#ffcf6f" : "#ffffff"); this.effects.burst(e.pos, crit ? "#ffcf6f" : col, crit ? 10 : 6); }
    const dx = e.pos.x - player.pos.x, dz = e.pos.z - player.pos.z;
    const d = Math.hypot(dx, dz) || 1;
    const kx = e.pos.x + (dx / d) * 0.4, kz = e.pos.z + (dz / d) * 0.4;
    if (!this.level.collidesWorld(kx, kz, e.radius)) { e.pos.x = kx; e.pos.z = kz; }
    if (!dead && weapon && weapon.element) this._applyElement(e, weapon.element, weapon.elementPower);
    // Legendary lifesteal: heal the player for a fraction of the damage dealt
    const ls = (player && player.lifesteal) || 0;
    if (ls > 0 && amt > 0 && player.hp < player.maxHp) {
      const heal = Math.max(1, Math.round(amt * ls));
      player.hp = Math.min(player.maxHp, player.hp + heal);
    }
    if (dead) { if (this.effects) this.effects.burst(e.pos, col, 16); this.onEnemyKilled(e); }
  }

  /** Staff/wand impact: AoE magic damage + element to everything in radius. */
  _explode(p, pos) {
    const col = ELEMENT_COLORS[p.element] || p.color || "#c77dff";
    if (this.effects) { this.effects.ring(pos, col, p.aoe); this.effects.burst({ x: pos.x, y: 1, z: pos.z }, col, 20); }
    for (const e of this.enemies) {
      if (e.dead) continue;
      const dd = Math.hypot(e.pos.x - pos.x, e.pos.z - pos.z);
      if (dd > p.aoe + e.radius) continue;
      const fall = 1 - (dd / (p.aoe + e.radius)) * 0.5;   // center hits harder
      const dmg = Math.max(1, Math.round(p.dmg * fall));
      const dead = e.applyDamage(dmg);
      if (this.effects) this.effects.text(e.pos, `${dmg}`, col);
      if (!dead && p.element) this._applyElement(e, p.element, p.elementPower);
      if (dead) { if (this.effects) this.effects.burst(e.pos, col, 12); this.onEnemyKilled(e); }
    }
    for (const d of this.destructibles) {
      if (!d.broken && Math.hypot(d.x - pos.x, d.z - pos.z) < p.aoe) this._break(d);
    }
  }

  /** Heavy boss slam: shockwave rings + a shower of sparks and debris at impact. */
  _bossSlamFx(e) {
    const fx = this.effects; if (!fx) return;
    const reach = (e.type.atkRange || 2.5) * 0.7;
    const ix = e.pos.x + Math.sin(e.yaw) * reach, iz = e.pos.z + Math.cos(e.yaw) * reach;
    const ground = { x: ix, y: 0.15, z: iz };
    fx.ring(ground, "#ffcf6f", (e.type.atkRange || 2.5) * 1.15);   // outer shockwave
    fx.ring(ground, "#ff7a3d", (e.type.atkRange || 2.5) * 0.6);    // inner blast
    fx.burst({ x: ix, y: 0.75, z: iz }, "#fff1b0", 34);           // bright sparks
    fx.burst({ x: ix, y: 0.7, z: iz }, "#ff9a3d", 22);            // embers
    fx.burst({ x: ix, y: 0.25, z: iz }, "#b58a4a", 20);          // dust / debris
  }

  enemyAttack(enemy, player) {
    if (enemy.boss) this._bossSlamFx(enemy);   // sparks + shockwave on every boss strike
    // block / parry if the player is holding block and facing the attacker
    const dx = enemy.pos.x - player.pos.x, dz = enemy.pos.z - player.pos.z;
    const d = Math.hypot(dx, dz) || 1;
    const facing = (dx / d) * Math.sin(player.yaw) + (dz / d) * Math.cos(player.yaw);
    if (player.blocking && facing > 0.25) {
      if (player.blockTimer < 0.3) {
        // PARRY — negate + stagger and knock the enemy back
        enemy.stagger = 0.8;
        const kx = enemy.pos.x + (dx / d) * 1.3, kz = enemy.pos.z + (dz / d) * 1.3;
        if (!this.level.collidesWorld(kx, kz, enemy.radius)) { enemy.pos.x = kx; enemy.pos.z = kz; }
        if (this.effects) { this.effects.text(player.pos, "PARRY!", "#ffe08a"); this.effects.burst(player.pos, "#ffe08a", 12); }
        return;
      }
      // BLOCK — greatly reduced damage; the armor remembers saving you
      const dead = player.takeHit(enemy.dmg * 0.25, this.effects);
      if (!dead && player.armor) recordSave(player.armor, player.classId);
      if (this.effects) { this.effects.text(player.pos, "BLOCK", "#9fd0ff"); this.effects.burst(player.pos, "#9fd0ff", 5); }
      if (dead) this.onPlayerDeath?.();
      return;
    }
    const dead = player.takeHit(enemy.dmg, this.effects);
    if (this.effects) this.effects.burst(player.pos, "#b5423a", 5);
    if (dead) this.onPlayerDeath?.();
  }

  /** A ranged/magic enemy looses a bolt toward the player's current position. */
  enemyRangedAttack(enemy, player) {
    const dx = player.pos.x - enemy.pos.x, dz = player.pos.z - enemy.pos.z;
    const yaw = Math.atan2(dx, dz);
    const el = enemy.type.element || null;
    const color = el ? (ELEMENT_COLORS[el] || "#c77dff") : (enemy.type.projColor || "#f0e6b0");
    const r = el ? 0.26 : 0.16;
    const mesh = new THREE.Mesh(PROJ_GEO, projMat(color));
    mesh.scale.setScalar(r);
    mesh.position.set(enemy.pos.x, 1.1, enemy.pos.z);
    this.scene.add(mesh);
    const speed = enemy.type.projSpeed || 12;
    this.projectiles.push({
      owner: "enemy", mesh, vx: Math.sin(yaw) * speed, vz: Math.cos(yaw) * speed,
      life: (enemy.type.atkRange + 5) / speed, dmg: enemy.dmg, crit: false,
      element: el, elementPower: enemy.type.elementPower || 1, aoe: enemy.type.aoe || 0, color,
      web: !!enemy.type.web, webRoot: enemy.type.webRoot || 0,
    });
    if (this.effects) this.effects.burst({ x: enemy.pos.x, y: 1.1, z: enemy.pos.z }, color, 6);
  }

  /** A direct enemy bolt strikes the player (block softens it; roll i-frames dodge it). */
  _enemyBolt(p, player) {
    const dmg = player.blocking ? p.dmg * 0.4 : p.dmg;
    const dead = player.takeHit(dmg, this.effects);
    if (this.effects) this.effects.burst(player.pos, p.color || "#b5423a", 6);
    // spider web: root the player for a random 3–6 seconds (a dodge-roll breaks free)
    if (p.web && player.hp > 0) {
      const dur = 3 + Math.random() * 3;
      player.webRoot = Math.max(player.webRoot || 0, dur);
      if (this.effects) { this.effects.text(player.pos, "WEBBED — roll to break free!", "#dfe8ff"); this.effects.ring(player.pos, "#dfe8ff", 1.1); }
    }
    if (dead) this.onPlayerDeath?.();
  }

  /** An enemy AoE bolt detonates; anyone (the player) within radius is caught. */
  _enemyExplode(p, pos) {
    const col = ELEMENT_COLORS[p.element] || p.color || "#c77dff";
    if (this.effects) { this.effects.ring({ x: pos.x, y: 1, z: pos.z }, col, p.aoe); this.effects.burst({ x: pos.x, y: 1, z: pos.z }, col, 16); }
    const pl = this.player;
    if (pl && !pl.dead && Math.hypot(pl.pos.x - pos.x, pl.pos.z - pos.z) < p.aoe + 0.5) {
      const dead = pl.takeHit(pl.blocking ? p.dmg * 0.45 : p.dmg, this.effects);
      if (dead) this.onPlayerDeath?.();
    }
  }

  update(dt) {
    for (const p of this.projectiles) {
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.z += p.vz * dt;
      p.life -= dt;
      const pos = p.mesh.position;
      let detonate = false;

      const g = this.level.worldToGrid(pos.x, pos.z);
      if (!this.level.hasFloorGrid(g.gx, g.gy)) { p.life = 0; if (p.aoe > 0) detonate = true; }

      if (p.owner === "enemy") {
        // enemy bolts hunt the PLAYER (rolling grants brief i-frames to dodge)
        const pl = this.player;
        if (p.life > 0 && pl && !pl.dead && Math.hypot(pl.pos.x - pos.x, pl.pos.z - pos.z) < 0.75) {
          if (p.aoe > 0) detonate = true; else this._enemyBolt(p, pl);
          p.life = 0;
        }
        if (p.aoe > 0 && p.life <= 0 && !detonate) detonate = true;
        if (detonate) this._enemyExplode(p, pos);
      } else {
        if (p.life > 0) {
          for (const e of this.enemies) {
            if (e.dead) continue;
            if (Math.hypot(e.pos.x - pos.x, e.pos.z - pos.z) < e.radius + 0.3) {
              if (p.aoe > 0) detonate = true;
              else this._damageEnemy(e, p.dmg, this.player, { element: p.element, elementPower: p.elementPower }, p.crit);
              p.life = 0; break;
            }
          }
        }
        if (p.life > 0) {
          for (const d of this.destructibles) {
            if (d.broken) continue;
            if (Math.hypot(d.x - pos.x, d.z - pos.z) < 0.8) {
              if (p.aoe > 0) detonate = true; else this._break(d);
              p.life = 0; break;
            }
          }
        }
        // an AoE bolt that reaches its max range detonates where it fizzles
        if (p.aoe > 0 && p.life <= 0 && !detonate) detonate = true;
        if (detonate) this._explode(p, { x: pos.x, y: 1, z: pos.z });
      }
    }
    pruneInPlace(this.projectiles, (p) => {
      if (p.life <= 0) { this.scene.remove(p.mesh); return false; }
      return true;
    });
  }
}
